import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { UserPlus, Trash, Package, Key, ShieldCheck, Prohibit, CheckCircle, PencilSimple, BellSlash, Buildings, X, DownloadSimple, UploadSimple, EnvelopeSimple } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import ConfirmDialog from "../components/ConfirmDialog";
import { Checkbox } from "../components/ui/checkbox";

const ROLES = [
  { v: "admin", label: "Administrateur" },
  { v: "technicien", label: "Technicien" },
  { v: "client", label: "Client" },
];

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("__all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", role: "client", password: "", company: "", phone: "" });
  const [editing, setEditing] = useState(null); // user being edited (null when closed)
  const [editForm, setEditForm] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const importInputRef = useRef(null);

  const load = async () => {
    const params = { page, per_page: PER_PAGE };
    if (search.trim()) params.q = search.trim();
    if (roleFilter !== "__all") params.role = roleFilter;
    const r = await api.get("/users", { params });
    const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
    setUsers(items);
    setTotal(Number(r.data.total ?? items.length));
    setPages(Math.max(1, Number(r.data.pages ?? 1)));
  };
  // v284 — Pagination serveur (25/page) + recherche & rôle envoyés au backend
  // pour permettre de retrouver des utilisateurs au-delà de la page courante.
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const PER_PAGE = 25;
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);
  // Debounce search + role filter, always jump back to page 1 on a query change.
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [search, roleFilter]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Utilisateur créé");
      setOpen(false);
      setForm({ email: "", full_name: "", role: "client", password: "", company: "", phone: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const [purgeCandidate, setPurgeCandidate] = useState(null);
  const [purgeStats, setPurgeStats] = useState(null);

  const remove = async (id) => {
    if (!window.confirm("Désactiver ce compte ?")) return;
    const r = await api.delete(`/users/${id}`);
    if (r.data?.hard) {
      toast.success("Compte supprimé définitivement");
      load();
    } else {
      // Soft-delete path — user has linked tickets. Offer a hard purge with snapshot preserved.
      const u = users.find((x) => x.id === id);
      if (u) {
        setPurgeCandidate(u);
        setPurgeStats({ linked_tickets: r.data?.linked_tickets || 0 });
      }
      toast.info(`Compte désactivé — ${r.data?.linked_tickets || 0} ticket(s) historique(s) préservé(s)`);
      load();
    }
  };

  const doPurge = async () => {
    if (!purgeCandidate) return;
    try {
      const r = await api.post(`/users/${purgeCandidate.id}/purge`);
      const { as_client, as_assignee } = r.data?.tickets_detached || {};
      toast.success(`${purgeCandidate.full_name} purgé — ${as_client || 0} ticket(s) détaché(s) (nom conservé)`);
      setPurgeCandidate(null);
      setPurgeStats(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      full_name: u.full_name || "",
      email: u.email || "",
      role: u.role || "client",
      company: u.company || "",
      phone: u.phone || "",
      mobile_phone: u.mobile_phone || "",
      address: u.address || "",
      postal_code: u.postal_code || "",
      city: u.city || "",
      country: u.country || "France",
      notes: u.notes || "",
      email_notifications: u.email_notifications !== false,
    });
  };

  const saveEdit = async (e) => {
    e?.preventDefault?.();
    if (!editing) return;
    try {
      await api.patch(`/users/${editing.id}`, editForm);
      toast.success("Fiche mise à jour");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de mise à jour");
    }
  };

  // v284 — Filtre local supprimé au profit du filtre serveur (page + search + role).
  const filtered = users;

  // Selectable IDs (exclude current admin to prevent self-selection)
  const selectableIds = filtered.filter((u) => u.id !== me?.id).map((u) => u.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleOne = (id, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };
  const toggleAll = (checked) => {
    setSelected(() => new Set(checked ? selectableIds : []));
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    try {
      const r = await api.post("/users/bulk-delete", { ids });
      const { hard_deleted, soft_deleted } = r.data;
      const bits = [];
      if (hard_deleted) bits.push(`${hard_deleted} purgé(s)`);
      if (soft_deleted) bits.push(`${soft_deleted} désactivé(s) (avec tickets)`);
      toast.success(bits.join(" · ") || "Aucune action");
      clearSelection();
      setBulkConfirmOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de suppression");
    }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/users/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `utilisateurs_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${filtered.length} utilisateurs exportés`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export impossible");
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/users/import.csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportReport(r.data);
      toast.success(`${r.data.created} créé(s) · ${r.data.updated} mis à jour · ${r.data.skipped} ignoré(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import impossible");
    } finally {
      setImportBusy(false);
      if (importInputRef.current) importInputRef.current.value = "";
    }
  };

  const downloadPasswordsReport = () => {
    if (!importReport?.new_passwords?.length) return;
    const rows = ["email;full_name;password", ...importReport.new_passwords.map(
      (p) => `${p.email};${p.full_name || ""};${p.password}`
    )];
    const blob = new Blob(["\ufeff" + rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nouveaux_mots_de_passe_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps">Équipe & clients</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Utilisateurs</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {me?.role === "admin" && (
            <Button variant="secondary" size="sm" onClick={exportCsv} data-testid="users-export-csv">
              <DownloadSimple size={14} className="mr-1.5"/> Export CSV
            </Button>
          )}
          {me?.role === "admin" && (
            <>
              <Button variant="secondary" size="sm" onClick={() => importInputRef.current?.click()}
                disabled={importBusy} data-testid="users-import-csv">
                <UploadSimple size={14} className="mr-1.5"/>{importBusy ? "Import…" : "Import CSV"}
              </Button>
              <input ref={importInputRef} type="file" accept=".csv,.txt,text/csv" className="hidden"
                data-testid="users-import-input"
                onChange={(e) => importCsv(e.target.files?.[0])}/>
            </>
          )}
          {me?.role === "admin" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn"><UserPlus size={16} className="mr-2"/> Ajouter</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Nouvel utilisateur</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Nom complet</Label>
                    <Input required data-testid="user-name" value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})}/>
                  </div>
                  <div>
                    <Label>Rôle</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                      <SelectTrigger data-testid="user-role"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input required type="email" data-testid="user-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}/>
                </div>
                <div>
                  <Label>Mot de passe temporaire</Label>
                  <Input required type="text" data-testid="user-password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Société</Label>
                    <Input data-testid="user-company" value={form.company} onChange={(e) => setForm({...form, company: e.target.value})}/>
                  </div>
                  <div>
                    <Label>Téléphone</Label>
                    <Input data-testid="user-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}/>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
                  <Button type="submit" data-testid="user-save">Créer</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </header>

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-3">
              <Input data-testid="user-search" placeholder="Rechercher nom, e-mail, société, téléphone…" value={search} onChange={(e) => setSearch(e.target.value)}/>
            </div>
            <div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger data-testid="user-role-filter"><SelectValue placeholder="Rôle"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">Tous les rôles</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                  <SelectItem value="technicien">Technicien</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {selected.size > 0 && me?.role === "admin" && (
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b bg-red-50 dark:bg-red-950/30" data-testid="users-bulk-bar">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-red-700 dark:text-red-300">{selected.size} utilisateur(s) sélectionné(s)</span>
                <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="users-bulk-clear" className="text-xs h-7">
                  <X size={12} className="mr-1"/> Annuler
                </Button>
              </div>
              <Button variant="destructive" size="sm" onClick={() => setBulkConfirmOpen(true)} data-testid="users-bulk-delete">
                <Trash size={14} className="mr-1.5"/> Supprimer la sélection
              </Button>
            </div>
          )}
          <div className="overflow-x-auto">
          <table className="w-full text-sm table-auto min-w-[900px]">
            <thead className="text-xs uppercase text-muted-foreground border-b">
              <tr>
                <th className="w-10 py-3 px-4">
                  {me?.role === "admin" ? (
                    <Checkbox
                      data-testid="users-select-all"
                      checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                      onCheckedChange={(v) => toggleAll(!!v)}
                      aria-label="Tout sélectionner"
                    />
                  ) : null}
                </th>
                <th className="text-left py-3 px-4">Nom</th>
                <th className="text-left py-3 px-4 whitespace-nowrap">Téléphone</th>
                <th className="text-left py-3 pl-8 pr-4">Email</th>
                <th className="text-left py-3 pl-8 pr-4">Rôle</th>
                <th className="text-left py-3 pl-6 pr-4">Société</th>
                <th className="text-left py-3 pl-6 pr-4">Statut</th>
                <th className="text-left py-3 pl-6 pr-4">2FA</th>
                <th className="text-right py-3 px-4 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className={`border-b ${selected.has(u.id) ? "bg-red-50/50 dark:bg-red-950/20" : ""}`} data-testid={`user-row-${u.id}`}>
                    <td className="py-3 px-4">
                      {me?.role !== "admin" ? null : u.id === me?.id ? (
                        <span title="Compte connecté — non sélectionnable" className="inline-block w-4 h-4 rounded border border-dashed border-muted-foreground/40"/>
                      ) : (
                        <Checkbox
                          data-testid={`user-select-${u.id}`}
                          checked={selected.has(u.id)}
                          onCheckedChange={(v) => toggleOne(u.id, !!v)}
                          aria-label={`Sélectionner ${u.full_name}`}
                        />
                      )}
                    </td>
                    <td className="py-3 px-4 font-medium">
                      {u.role === "client" ? (
                        <Link to={`/clients/${u.id}/services`}
                          className="text-primary hover:underline"
                          data-testid={`user-name-${u.id}`}
                          title="Voir la fiche client">
                          {u.full_name}
                        </Link>
                      ) : (
                        <span>{u.full_name}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 mono text-xs whitespace-nowrap">
                      {u.phone ? (
                        <a href={`tel:${u.phone.replace(/\s+/g, "")}`} className="text-primary hover:underline">{u.phone}</a>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-3 pl-8 pr-4 mono text-xs">
                      <div className="flex items-center gap-1.5">
                        <span>{u.email}</span>
                        {u.email_notifications === false && (
                          <span title="Notifications email désactivées (adresse sans réponse ou refus client)"
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[0.6rem] font-medium bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800">
                            <BellSlash size={10} weight="fill" className="mr-1"/> Muet
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 pl-8 pr-4"><Badge variant="outline" className="uppercase text-[0.65rem]">{u.role}</Badge></td>
                    <td className="py-3 pl-6 pr-4">
                      {u.company_id ? (
                        <Link to={`/societes/${u.company_id}`} className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`user-company-link-${u.id}`}>
                          <Buildings size={12}/> {u.company || "société"}
                        </Link>
                      ) : (
                        u.company || <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-3 pl-6 pr-4">
                      {u.active
                        ? <span className="text-emerald-600">Actif</span>
                        : <span className="text-muted-foreground">Suspendu</span>}
                      {u.role === "client" && u.email_verified === false && (
                        <div title="Le client n'a pas encore cliqué sur le lien d'activation reçu par email. Il ne peut pas se connecter tant que ce n'est pas fait."
                          className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded text-[0.6rem] font-medium bg-orange-100 text-orange-800 border border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800">
                          Non vérifié
                        </div>
                      )}
                    </td>
                    <td className="py-3 pl-6 pr-4">{u.totp_enabled ? <span className="text-emerald-600">✓</span> : <span className="text-muted-foreground">—</span>}</td>
                    <td className="py-3 px-2 text-right whitespace-nowrap">
                      {u.role === "client" && (
                        <Link to={`/clients/${u.id}/services`} data-testid={`user-services-${u.id}`}>
                          <Button size="icon" variant="ghost" title="Services souscrits" className="h-8 w-8">
                            <Package size={15}/>
                          </Button>
                        </Link>
                      )}
                      {me?.role === "admin" && (<>
                      <Button size="icon" variant="ghost" title="Éditer la fiche" className="h-8 w-8"
                        onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}>
                        <PencilSimple size={15}/>
                      </Button>
                      <Button size="icon" variant="ghost" title="Réinitialiser le mot de passe" className="h-8 w-8" onClick={async () => {
                        const pwd = window.prompt("Nouveau mot de passe temporaire");
                        if (!pwd) return;
                        try { await api.post(`/users/${u.id}/reset-password`, { password: pwd }); toast.success("Mot de passe réinitialisé"); }
                        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                      }} data-testid={`user-pwd-${u.id}`}><Key size={15}/></Button>
                      <Button size="icon" variant="ghost" title="Réinitialiser la 2FA" className="h-8 w-8" onClick={async () => {
                        if (!window.confirm(`Réinitialiser la 2FA de ${u.full_name} ?`)) return;
                        await api.post(`/users/${u.id}/reset-2fa`); toast.success("2FA réinitialisée"); load();
                      }} data-testid={`user-2fa-${u.id}`}><ShieldCheck size={15}/></Button>
                      {u.role === "client" && u.email_verified === false && (
                        <>
                          <Button size="icon" variant="ghost" title="Forcer l'activation (marquer email vérifié)" className="h-8 w-8 text-emerald-600 hover:text-emerald-700"
                            onClick={async () => {
                              if (!window.confirm(`Forcer l'activation de ${u.full_name} ? Le compte sera immédiatement utilisable sans validation email.`)) return;
                              try { await api.post(`/users/${u.id}/force-verify`); toast.success("Compte activé"); load(); }
                              catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                            }} data-testid={`user-force-verify-${u.id}`}><CheckCircle size={15} weight="fill"/></Button>
                          <Button size="icon" variant="ghost" title="Renvoyer l'email d'activation" className="h-8 w-8"
                            onClick={async () => {
                              try { await api.post(`/users/${u.id}/resend-verify`); toast.success("Email d'activation renvoyé"); }
                              catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                            }} data-testid={`user-resend-verify-${u.id}`}><EnvelopeSimple size={15}/></Button>
                        </>
                      )}
                      <Button size="icon" variant="ghost"
                        title={u.email_notifications === false ? "Réactiver les notifications email" : "Couper les notifications email"}
                        className="h-8 w-8" onClick={async () => {
                        const next = !(u.email_notifications === false);
                        try {
                          await api.patch(`/users/${u.id}`, { email_notifications: !next });
                          toast.success(next ? "Notifications coupées" : "Notifications réactivées");
                          load();
                        } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
                      }} data-testid={`user-notif-${u.id}`}>
                        <BellSlash size={15} className={u.email_notifications === false ? "text-amber-600" : "text-muted-foreground"}/>
                      </Button>
                      {u.active ? (
                        <Button size="icon" variant="ghost" title="Suspendre" className="h-8 w-8" onClick={async () => {
                          await api.post(`/users/${u.id}/suspend`); toast.success("Compte suspendu"); load();
                        }} data-testid={`user-suspend-${u.id}`}><Prohibit size={15}/></Button>
                      ) : (
                        <Button size="icon" variant="ghost" title="Réactiver" className="h-8 w-8" onClick={async () => {
                          await api.post(`/users/${u.id}/activate`); toast.success("Compte réactivé"); load();
                        }} data-testid={`user-activate-${u.id}`}><CheckCircle size={15}/></Button>
                      )}
                      <Button size="icon" variant="ghost" title="Supprimer" className="h-8 w-8" onClick={async () => {
                        if (!window.confirm(`Supprimer ${u.full_name} ?\n\nSi le compte a des tickets, il sera désactivé (nom préservé sur l'historique).\nSinon il sera supprimé définitivement.`)) return;
                        const r = await api.delete(`/users/${u.id}?hard=true`);
                        if (r.data?.hard) {
                          toast.success("Compte supprimé définitivement");
                        } else {
                          setPurgeCandidate(u);
                          setPurgeStats({ linked_tickets: r.data?.linked_tickets || 0 });
                          toast.info(`Compte désactivé — ${r.data?.linked_tickets || 0} ticket(s) historique(s) préservé(s)`);
                        }
                        load();
                      }} data-testid={`user-del-${u.id}`}><Trash size={15}/></Button>
                      </>)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t text-sm">
            <div className="text-muted-foreground" data-testid="users-total">{total} utilisateur(s)</div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="users-prev-page">Précédent</Button>
              <span className="mono" data-testid="users-page-indicator">Page {page} / {pages}</span>
              <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="users-next-page">Suivant</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Modifier la fiche · {editing?.full_name}</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-4" data-testid="edit-user-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nom complet</Label>
                  <Input required data-testid="edit-full-name" value={editForm.full_name}
                    onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}/>
                </div>
                <div>
                  <Label>Rôle</Label>
                  <Select value={editForm.role} onValueChange={(v) => setEditForm({...editForm, role: v})}>
                    <SelectTrigger data-testid="edit-role"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Email</Label>
                  <Input required type="email" data-testid="edit-email" value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}/>
                </div>
                <div>
                  <Label>Société</Label>
                  <Input data-testid="edit-company" value={editForm.company}
                    onChange={(e) => setEditForm({...editForm, company: e.target.value})}/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Téléphone fixe</Label>
                  <Input type="tel" data-testid="edit-phone" value={editForm.phone}
                    onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                    placeholder="04 74 12 34 56"/>
                </div>
                <div>
                  <Label>Téléphone portable</Label>
                  <Input type="tel" data-testid="edit-mobile-phone" value={editForm.mobile_phone}
                    onChange={(e) => setEditForm({...editForm, mobile_phone: e.target.value})}
                    placeholder="06 12 34 56 78"/>
                </div>
              </div>
              <div>
                <Label>Adresse</Label>
                <Input data-testid="edit-address" value={editForm.address}
                  onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                  placeholder="12 rue de la République"/>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Code postal</Label>
                  <Input data-testid="edit-postal-code" value={editForm.postal_code}
                    onChange={(e) => setEditForm({...editForm, postal_code: e.target.value})}
                    placeholder="69001"/>
                </div>
                <div className="col-span-2">
                  <Label>Ville</Label>
                  <Input data-testid="edit-city" value={editForm.city}
                    onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                    placeholder="Lyon"/>
                </div>
              </div>
              <div>
                <Label>Pays</Label>
                <Input data-testid="edit-country" value={editForm.country}
                  onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                  placeholder="France"/>
              </div>
              <div>
                <Label>Notes internes</Label>
                <Textarea data-testid="edit-notes" rows={4} value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Informations complémentaires (visibles uniquement par le staff)"/>
              </div>
              <div className="flex items-center gap-3 border-t pt-4">
                <input type="checkbox" id="edit-notifs" data-testid="edit-notifs"
                  checked={editForm.email_notifications !== false}
                  onChange={(e) => setEditForm({...editForm, email_notifications: e.target.checked})}
                  className="accent-primary"/>
                <label htmlFor="edit-notifs" className="text-sm">
                  Recevoir les notifications email
                  <span className="block text-xs text-muted-foreground">Décochez pour couper tous les envois automatiques (bounces, no-reply, refus client)</span>
                </label>
              </div>
              <DialogFooter>
                <Button type="button" variant="secondary" onClick={() => setEditing(null)} data-testid="edit-cancel">Annuler</Button>
                <Button type="submit" data-testid="edit-save">Enregistrer</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={bulkConfirmOpen}
        onOpenChange={setBulkConfirmOpen}
        title={`Supprimer ${selected.size} utilisateur(s) ?`}
        description="Les comptes ayant des tickets seront désactivés (leur nom reste visible sur l'historique). Ceux sans aucun ticket seront purgés définitivement."
        confirmLabel="Confirmer la suppression"
        danger
        onConfirm={bulkDelete}
      />

      {/* Import CSV report dialog */}
      <Dialog open={!!importReport} onOpenChange={(v) => { if (!v) setImportReport(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Rapport d&apos;import CSV</DialogTitle>
          </DialogHeader>
          {importReport && (
            <div className="space-y-4" data-testid="users-import-report">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border p-3 text-center bg-emerald-50 dark:bg-emerald-950/30">
                  <div className="text-2xl font-bold text-emerald-600">{importReport.created}</div>
                  <div className="text-xs uppercase text-muted-foreground">créés</div>
                </div>
                <div className="rounded-lg border p-3 text-center bg-blue-50 dark:bg-blue-950/30">
                  <div className="text-2xl font-bold text-blue-600">{importReport.updated}</div>
                  <div className="text-xs uppercase text-muted-foreground">mis à jour</div>
                </div>
                <div className="rounded-lg border p-3 text-center bg-muted/40">
                  <div className="text-2xl font-bold text-muted-foreground">{importReport.skipped}</div>
                  <div className="text-xs uppercase text-muted-foreground">ignorés</div>
                </div>
              </div>

              {importReport.new_passwords?.length > 0 && (
                <div className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">
                      {importReport.new_passwords.length} nouveau(x) mot(s) de passe temporaire(s)
                    </div>
                    <Button size="sm" variant="secondary" onClick={downloadPasswordsReport}
                      data-testid="users-download-passwords">
                      <DownloadSimple size={14} className="mr-1.5"/> Télécharger CSV
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Communiquez ces mots de passe aux utilisateurs — ils devront les changer à leur première connexion.
                  </p>
                  <div className="max-h-48 overflow-y-auto text-xs mono border-t pt-2">
                    {importReport.new_passwords.map((p, i) => (
                      <div key={i} className="flex justify-between gap-3 py-1 border-b last:border-b-0">
                        <span className="truncate">{p.email}</span>
                        <span className="font-semibold">{p.password}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importReport.errors?.length > 0 && (
                <div className="border border-destructive/40 rounded-lg p-3 bg-destructive/5">
                  <div className="text-sm font-semibold text-destructive mb-1.5">
                    {importReport.errors.length} erreur(s)
                  </div>
                  <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
                    {importReport.errors.map((e, i) => (
                      <li key={i}>Ligne {e.line} · {e.email || "—"} · {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <DialogFooter>
                <Button onClick={() => setImportReport(null)} data-testid="users-import-close">Fermer</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!purgeCandidate} onOpenChange={(v) => { if (!v) { setPurgeCandidate(null); setPurgeStats(null); } }}>
        <DialogContent className="max-w-md" data-testid="purge-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-red-700 dark:text-red-400">Purger définitivement le compte</DialogTitle>
          </DialogHeader>
          {purgeCandidate && (
            <div className="space-y-4">
              <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-950/20 p-3 text-sm">
                <p className="font-medium">Compte à purger :</p>
                <p className="mono text-xs mt-1">{purgeCandidate.full_name} — {purgeCandidate.email}</p>
              </div>
              <div className="text-sm space-y-2">
                <p>Le compte est actuellement <b>désactivé</b> car il a <b>{purgeStats?.linked_tickets || 0} ticket(s)</b> historique(s). Vous pouvez :</p>
                <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                  <li><b>Le laisser</b> désactivé : le nom reste attaché à ses tickets, l&apos;email ne peut pas être ré-utilisé pour un nouveau compte</li>
                  <li><b>Le purger</b> : le nom <span className="text-emerald-600 font-medium">est préservé en snapshot</span> sur chaque ticket historique, mais le compte utilisateur est <b>supprimé</b> et l&apos;email <b>libéré</b> pour un nouvel enregistrement</li>
                </ul>
              </div>
              <div className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
                Les tickets resteront lisibles avec le nom &laquo;&nbsp;{purgeCandidate.full_name}&nbsp;&raquo;. Cette action est <b>irréversible</b>.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => { setPurgeCandidate(null); setPurgeStats(null); }} data-testid="purge-cancel">
              Garder désactivé
            </Button>
            <Button variant="destructive" onClick={doPurge} data-testid="purge-confirm">
              <Trash size={14} className="mr-2"/> Purger définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
